import {createSelector} from '@reduxjs/toolkit'

import {uniqWith} from '@shared/helpers/array'
import {groupBy, sortBy} from '@shared/helpers/collection'
import {pipe} from '@shared/helpers/function'
import {eq, isEmpty, isNil} from '@shared/helpers/lang'
import {toPairs} from '@shared/helpers/object'

import {
    CardOwnerGroup,
    CreditCardBillDiscountEvent,
    EasyCardItemType,
    PromotionUnApplicableReason,
    PaymentsCardCodeType,
    UNAVAILABLE_REASON_TYPE,
} from '@web/core/constants'

import {
    convertFreeInstallmentBenefit,
    makeInterestFreeInstallmentsByCompanyCode,
    promotionUtil,
    sortCardEasyPaysItems,
    promotionGroupItemConverter,
    isVaildAsSpecialRateInstallment,
} from './helpers'
import {CardPromotionInfoSelectorReturnType, RootState} from './interfaces'
import {CardView, CreditCardBillDiscount, InterestFreeInstallment} from './interfaces/card'
import {GroupItem as NullableGroupItem} from './interfaces/selector'

const isCorporationCard = eq(CardOwnerGroup.CORPORATION)

export const cardSelector = (state: RootState) => state.card

/**
 * TODO:(카드프로모션 통합) cardPromotionInfoSelector에서 가져오도록 변경필요
 * 카드 slice > promotion interface를 nullable > optional 전환하는 중에
 * 영향범위 축소를 위해 selector에서 내보내는 interface는 그대로 맞추기 위해 converter를 적용함
 * PR: https://oss.fin.navercorp.com/payment-fe/order-web/pull/17759
 * 관련 QA: https://oss.fin.navercorp.com/Product-Planning/Order_QA/issues/10958
 * 그로인해 selector에서 내보내는 객체가 매번 새로운 객체로 나오기 때문에 카드프로모션을 바꾸지 않았음에도
 * selected 값이 변경되어 useEffect hook이 실행되기 때문에 converter 적용하지 않은 값을 쓰기 위해 card slice값 직접 조회해서 사용
 */
export const promotionSelector = createSelector(cardSelector, ({promotion: {groups, selectedInfo}}) => {
    const promotionGroups = groups.filter((group) => !isEmpty(group.cardPromotions))
    const selectedIndex = selectedInfo?.cardProductNameCode
        ? promotionGroups.findIndex((group) => group.card.productNameCode === selectedInfo?.cardProductNameCode)
        : promotionGroups.findIndex(
              (group) => group.card.companyCode === selectedInfo?.cardCompanyCode && isNil(group.card?.productNameCode),
          )

    const selected = promotionGroups[selectedIndex]

    return {
        selected,
    }
})

/** 일반 카드사 목록별 할부 정보 (기본 12개월) */
export const interestFreeInstallmentsByCompanyCodeSelector = createSelector(
    (state: RootState) => state.card.creditCard.companies,
    (state: RootState) => state.card.creditCard.interestFreeInstallments,
    (state: RootState) => state.card.creditCard.interestFreeStatus,
    (state: RootState) => state.card.creditCard.limitedInstallmentMonth,
    (companies, interestFreeInstallments, interestFreeStatus, limitedInstallmentMonth) => {
        return makeInterestFreeInstallmentsByCompanyCode({
            cardCompanies: companies.map(({code, name, baseInstallmentInfo}) => ({
                code,
                name,
                baseInstallmentInfo,
            })),
            interestFreeInstallments,
            interestFreeStatus,
            limitedInstallmentMonth,
        })
    },
)

/** 간편카드사 목록별 할부 정보 (기본 12개월) */
export const interestFreeInstallmentsByEasyCadeSelector = createSelector(
    (state: RootState) => state.card.cardEasyPay.items,
    (state: RootState) => state.card.cardEasyPay.interestFreeInstallments,
    (state: RootState) => state.card.cardEasyPay.interestFreeStatus,
    (state: RootState) => state.card.cardEasyPay.limitedInstallmentMonth,
    (cardEasyPays, interestFreeInstallments, interestFreeStatus, limitedInstallmentMonth) => {
        return makeInterestFreeInstallmentsByCompanyCode({
            cardCompanies: uniqWith<CardView>(
                ({companyCode: companyCodeA}, {companyCode: companyCodeB}) => companyCodeA === companyCodeB,
                cardEasyPays,
            ).map(({companyCode, baseInstallmentInfo}) => ({
                code: companyCode,
                name: '',
                baseInstallmentInfo,
            })),
            interestFreeInstallments,
            interestFreeStatus,
            limitedInstallmentMonth,
        })
    },
)

/** 카드사 목록별 할부 그룹 */
export const interestFreeInstallmentsByCadeCodeGroupSelector = createSelector(
    (state: RootState) => state.card.cardEasyPay.interestFreeInstallments,
    (state: RootState) => state.card.creditCard.interestFreeInstallments,
    (cardEasyPayInterestFreeInstallments, creditCardInterestFreeInstallments) => {
        const easyCardCompanyCodeGroup = pipe(
            sortBy<InterestFreeInstallment>((v) => v.installmentMonth),
            groupBy<InterestFreeInstallment>((v) => v.cardCompanyCode),
        )(cardEasyPayInterestFreeInstallments)

        const cardCompanyCodeGroup = pipe(
            sortBy<InterestFreeInstallment>((v) => v.installmentMonth),
            groupBy<InterestFreeInstallment>((v) => v.cardCompanyCode),
        )(creditCardInterestFreeInstallments)

        return {
            easyCardCompanyCodeGroup,
            cardCompanyCodeGroup,
        }
    },
)

/** 제휴 카드사 안내 selector */
export const partnershipInfoSelector = createSelector(
    (state: RootState) => state.card.partnership,
    ({companyCodes, containsShinhanPartnershipCard}) => {
        return {
            /** 제휴 카드사 목록 */
            companyCodes,
            /** 제휴 카드사 안내 여부 */
            hasPartnershipCompany: !isEmpty(companyCodes),
            /** 신한 제휴 카드 포함 여부 */
            containsShinhanPartnershipCard,
        }
    },
)

/** 간편카드 할부 정보 */
export const cardEasyInfoFreeInstallmentBenefitInfoSelector = createSelector(
    (state: RootState) => state.card.cardEasyPay.interestFreeInstallments,
    (interestFreeInstallments) => {
        return convertFreeInstallmentBenefit(interestFreeInstallments)
    },
)

/** 일반카드 할부 정보 */
export const creditCardInfoFreeInstallmentBenefitInfoSelector = createSelector(
    (state: RootState) => state.card.creditCard.interestFreeInstallments,
    (interestFreeInstallments) => {
        return convertFreeInstallmentBenefit(interestFreeInstallments)
    },
)

/** 간편카드 유이자 할부 정보 */
export const cardEasyInfoInstallmentBenefitInfoSelector = createSelector(
    (state: RootState) => state.card.cardEasyPay.interestFreeInstallments,
    (interestFreeInstallments) => {
        return interestFreeInstallments.filter(({interestFreeType}) => interestFreeType === 'NONE')
    },
)

/** 혜택안내 가능한 간편카드결제 특별금리 할부 데이터 */
export const cardEasyPaySpecialRateInstallmentBenefitSelector = createSelector(
    cardEasyInfoInstallmentBenefitInfoSelector,
    (interestFreeInstallmentsByCardEasyPay) => {
        // 혜택으로 노출가능한 가능한 특별 금리 할부 데이터
        return interestFreeInstallmentsByCardEasyPay.filter(isVaildAsSpecialRateInstallment)
    },
)

/** 일반카드 유이자 할부 정보 */
export const creditCardInfoInstallmentBenefitInfoSelector = createSelector(
    (state: RootState) => state.card.creditCard.interestFreeInstallments,
    (interestFreeInstallments) => {
        return interestFreeInstallments.filter(({interestFreeType}) => interestFreeType === 'NONE')
    },
)

/** 혜택안내 가능한 일반카드 특별금리 할부 정보 */
export const creditCardSpecialRateInstallmentBenefitSelector = createSelector(
    creditCardInfoInstallmentBenefitInfoSelector,
    (interestFreeInstallmentsByCreditCard) => {
        // 혜택으로 노출가능한 가능한 특별 금리 할부 데이터
        return interestFreeInstallmentsByCreditCard.filter(isVaildAsSpecialRateInstallment)
    },
)

/** 선택된 카드사 프로모션 selector */
export const cardPromotionInfoSelector = createSelector(
    (state: RootState) => state.card.promotion,
    ({groups, selectedInfo, status, apiStatus, skipInfo}): CardPromotionInfoSelectorReturnType => {
        const promotionGroups = groups.filter((group) => !isEmpty(group.cardPromotions))

        const selectedIndex = selectedInfo?.cardProductNameCode
            ? promotionGroups.findIndex((group) => group.card.productNameCode === selectedInfo?.cardProductNameCode)
            : promotionGroups.findIndex(
                  (group) =>
                      group.card.companyCode === selectedInfo?.cardCompanyCode && isNil(group.card?.productNameCode),
              )

        const selectedGroupInfo = promotionGroups[selectedIndex]
            ? promotionGroupItemConverter(promotionGroups[selectedIndex])
            : null

        const selectedMinPayAmount = selectedGroupInfo ? promotionUtil.getValidMinPayAmount(selectedGroupInfo) : 0

        return {
            reloadablePromotionGroups: groups.map((group) => promotionGroupItemConverter(group)),
            status,
            apiStatus,
            selected: selectedGroupInfo ?? null,
            promotionGroups: [
                ...promotionGroups
                    .filter((promotionGroup) => !promotionGroup.unApplicableReason)
                    .map((group) => promotionGroupItemConverter(group)),
                ...promotionGroups
                    .filter((promotionGroup) => promotionGroup.unApplicableReason)
                    .map((group) => promotionGroupItemConverter(group)),
            ],
            /** 선택된 프로모션 그룹의 프로모션들 중 최소주문금액 */
            selectedMinPayAmount,
            skipInfo,
            shouldPromotionInfoSkip: Object.values(skipInfo).some((info) => info),
            /** 선택된 프로모션 그룹의 적용 불가능 사유 */
            selectedUnApplicableReason: {
                isMinimumProductPrice:
                    !!selectedGroupInfo &&
                    selectedGroupInfo.unApplicableReason === PromotionUnApplicableReason.MINIMUM_PRODUCT_PRICE,
                isTooManyProduct:
                    !!selectedGroupInfo &&
                    selectedGroupInfo.unApplicableReason === PromotionUnApplicableReason.TOO_MANY_PROMOTION,
            },
        }
    },
)

/** 간편카드 정렬로직 관리 selector */
const cardEasyPayItemsSelector = createSelector(
    (state: RootState) => state.card.cardEasyPay.items,
    sortCardEasyPaysItems<CardView>(
        (cardItem) => (cardItem?.payService?.payable && cardItem?.payService?.supportedMerchant) ?? true,
    ),
)

const isApplicableEasyCard = (card: CardView, selectedCardPromotion: NullableGroupItem) =>
    promotionUtil.isApplicableEasyCard({
        targetCard: {
            companyCode: card.companyCode,
            type: card.type,
            ownerType: card.ownerType,
            issuerCode: card.issuerCode,
            companyId: card.companyId,
            productCode: card.productCode,
        },
        promotionCardInfo: {
            companyCode: selectedCardPromotion.card.companyCode,
            companyName: selectedCardPromotion.card.companyName,
            detailInfos: selectedCardPromotion.card.detailInfos.map(
                ({companyId, issuerCode, types, ownerTypes, productCodes}) => {
                    return {
                        companyId,
                        issuerCode,
                        types,
                        ownerTypes,
                        productCodes,
                    }
                },
            ),
            productNameCode: selectedCardPromotion.card.productNameCode,
            productName: selectedCardPromotion.card.productName,
        },
    })

/** 간편 카드 정보 selector */
export const cardEasyPayInfoSelector = createSelector(
    interestFreeInstallmentsByEasyCadeSelector,
    cardEasyPayItemsSelector,
    (state: RootState) => state.card.cardEasyPay,
    cardPromotionInfoSelector,
    (
        interestFreeInstallmentsByCompanyCode, // 카드사별 전체 할부 정보
        items,
        {
            selected: {id, installment, itemType, usingCardCorpPoint},
            interestFreeStatus,
            limitedInstallmentMonth,
            usablePointByCardCorp,
            payService,
        },
        {selected: selectedCardPromotion},
    ) => {
        const cards = items.map((card) => {
            /** 여러 사용불가 요소가 겹쳐있을 때는 비지니스적인 사유를 우선시하고, 시스템 점검은 가장 마지막으로 체크한다 */
            const unavailableReason = (() => {
                if (selectedCardPromotion && !isApplicableEasyCard(card, selectedCardPromotion)) {
                    return UNAVAILABLE_REASON_TYPE.INVALID_SELECTED_PROMOTION_CARD
                }
                // 가맹점 미지원 카드
                if (!card.payService.supportedMerchant) {
                    return UNAVAILABLE_REASON_TYPE.MERCHANT_NOT_SUPPORTING_CARD
                }

                // 원천사 카드개별 점검
                if (!card.payService.payable) {
                    return UNAVAILABLE_REASON_TYPE.CARD_COMPANY_UNDER_MAINTENANCE
                }

                return undefined
            })()

            return {
                ...card,
                unavailableReason,
                /** 카드사 포인트 사용가능여부 */
                corpPointAvailable:
                    (usablePointByCardCorp?.[card.companyCode] &&
                        card.payService.supportedMerchant &&
                        card.payService.payable) ??
                    false,
            }
        })

        const selectedCardIndex = cards.findIndex((card) => id === card.id)
        const selectedCardInfo = cards[selectedCardIndex] ?? null
        const cardCompanyCode = selectedCardInfo?.companyCode ?? null
        const installmentsByCardCompanyCode =
            (cardCompanyCode && interestFreeInstallmentsByCompanyCode[cardCompanyCode]) || []
        const installmentInfo =
            installmentsByCardCompanyCode.find(({installmentMonth}) => eq(`${installmentMonth}`, installment)) ?? null
        const noCorpPointAvailableCard = !cards.some((card) => usablePointByCardCorp?.[card.companyCode])

        return {
            /** 간편 카드결제 등록 여부 */
            isRegistered: !isEmpty(cards),
            /** 간편결제카드 리스트 */
            cards,
            /** 할부 리스트 */
            installmentsByCompanyCode: interestFreeInstallmentsByCompanyCode,
            /** 네이버페이 사용가능 여부(점검 및 서킷 발동) */
            naverPayService: {
                available: payService.payable,
                maintenanceSchedule: payService.maintenance,
            },
            /** 할부 상태 */
            interestFreeStatus,
            /** 제한된 할부 개월수 */
            limitedInstallmentMonth,
            /** 카드사 포인트 사용가능 카드 없음 **/
            noCorpPointAvailableCard,
            /** 카드사 포인트 디스플레이 */
            corpPointSupported: !isEmpty(usablePointByCardCorp),
            /** 선택된 카드 **/
            selected: {
                /** 선택된 카드 간편결제 카드 index */
                index: selectedCardIndex,
                /** 선택된 카드 간편결제 카드 정보 */
                info: selectedCardInfo,
                /** 선택된 카드 타입 */
                itemType,
                /** 선택된 카드 간편결제 카드 ID */
                id,
                /** 선택된 카드 할부기간 */
                installment,
                /** 선택된 카드 할부정보 */
                installmentInfo,
                /** 선택된 카드사의 할부 정보 */
                installmentsByCardCompanyCode,
                /** 선택된 카드사의 무이자 할부 정보 */
                interestFreeInstallmentsByEasyCadeCode:
                    (cardCompanyCode && interestFreeInstallmentsByCompanyCode[cardCompanyCode]) || [],
                /** 선택된 카드 법인카드 여부 */
                isCorporationCard: !!selectedCardInfo && isCorporationCard(selectedCardInfo.ownerType),
                /** 선택된 카드가 유효한지 여부
                 * - 카드추가, 카드배너 제외
                 **/
                isValid: !!selectedCardInfo && itemType === EasyCardItemType.CARD,
                /** 카드사 포인트 사용가능여부 */
                corpPointAvailable: cards[selectedCardIndex]?.corpPointAvailable ?? false,
                /** 카드사 포인트 사용 여부 */
                usingCardCorpPoint,
                /** 가맹점 지원카드 여부 **/
                supportedMerchant: selectedCardInfo?.payService?.supportedMerchant ?? true,
            },
        }
    },
)

/** 신용 카드 정보 selector */
export const creditCardInfoSelector = createSelector(
    interestFreeInstallmentsByCompanyCodeSelector,
    (state: RootState) => state.card.creditCard,
    (
        interestFreeInstallmentsByCompanyCode,
        {
            availableTypes,
            companies,
            selected: {type, companyCode, installment, appCardInfo},
            interestFreeStatus,
            limitedInstallmentMonth,
        },
    ) => {
        const installmentsByCardCompanyCode: InterestFreeInstallment[] = companyCode
            ? (interestFreeInstallmentsByCompanyCode[companyCode] ?? [])
            : []
        const installmentInfo =
            installmentsByCardCompanyCode.find(({installmentMonth}) => eq(`${installmentMonth}`, installment)) ?? null

        return {
            /** 가능한 카드 구분 */
            availableTypes,
            /** 간편결제카드 리스트 */
            companies,
            /** 할부 리스트 */
            installmentsByCompanyCode: interestFreeInstallmentsByCompanyCode,
            /** 할부 상태 */
            interestFreeStatus,
            /** 제한된 할부 개월수 */
            limitedInstallmentMonth,
            selected: {
                /** 카드 구분 개인카드 | 법인카드 */
                type,
                /** 선택된 카드사 코드 */
                companyCode,
                /** 할부 기간 */
                installment,
                /** 선택된 카드 할부정보 */
                installmentInfo,
                /** 선택된 카드사의 할부 정보 */
                installmentsByCardCompanyCode,
                /** 선택된 카드사의 무이자 할부 정보 */
                interestFreeInstallmentsByCadeCode: companyCode
                    ? (interestFreeInstallmentsByCompanyCode[companyCode] ?? [])
                    : [],
                /** 선택된 카드 법인카드 여부 */
                isCorporationCard: isCorporationCard(type),
                /** 선택된 카드의 앱카드 정보 */
                appCardInfo,
            },
        }
    },
)

/** 누리 카드 정보 selector */
export const welfareCardInfoSelector = createSelector(
    (state: RootState) => state.card.welfareCard,
    ({companies, selected: {companyCode}}) => {
        return {
            /** 누리 카드 리스트 */
            companies,
            selected: {
                /** 선택된 카드사 코드 */
                companyCode,
            },
        }
    },
)

type CardCompanyCodeMapType = Record<PaymentsCardCodeType, CreditCardBillDiscount[]>
/** 신용카드 청구서 할인 정보 Selector */
export const creditCardBillDiscountInfoSelector = createSelector(
    (state: RootState) => state.card.creditCardBillDiscounts,
    (
        creditCardBillDiscounts,
    ): {
        /** 신용카드 청구서 할인 리스트 */
        creditCardBillDiscounts: CreditCardBillDiscount[]
        /** 카드사 코드별 신용카드 청구서 할인 리스트 */
        cardCompanyCodeMap: CardCompanyCodeMapType
        /** 카드사 코드별 신용카드 청구서 할인 리스트 */
        cardCompanyCodes: [string, CreditCardBillDiscount[]][]
        /** 할인정보 내 청구할인행사 포함여부 */
        isDiscountIncluded: boolean
    } => {
        const cardCompanyCodeMap = creditCardBillDiscounts.reduce<CardCompanyCodeMapType>(
            (acc, creditCardBillDiscount) => {
                const {cardCompanyCode} = creditCardBillDiscount
                if (acc[cardCompanyCode]) {
                    acc[cardCompanyCode].push(creditCardBillDiscount)
                } else {
                    acc[cardCompanyCode] = [creditCardBillDiscount]
                }
                return acc
            },
            {} as CardCompanyCodeMapType,
        )
        return {
            creditCardBillDiscounts,
            cardCompanyCodeMap,
            cardCompanyCodes: toPairs(cardCompanyCodeMap),
            isDiscountIncluded: creditCardBillDiscounts.some(
                (discountInfo) => discountInfo.eventType === CreditCardBillDiscountEvent.CS,
            ),
        }
    },
)

/** 간편카드결제 > 선택된 카드 타입 수수료 Selector */
export const cardEasyPayCommissionSelector = createSelector(
    (state: RootState) => state.card.commission,
    (state: RootState) => state.card.cardEasyPay,
    (commission, {selected: {id}, items}) => {
        const selectedCardType = items.find((item) => item.id === id)?.type

        return selectedCardType ? commission[selectedCardType] : 0
    },
)
